<script setup lang="ts">
import { computed } from "vue";
import { UiText, UiIcon, UiButton } from "@shopexperts/shared";
import ClientProjectLeadCard from "./ClientProjectLeadCard.vue";
import ExpertProjectCard from "./ExpertProjectCard.vue";
import type {
  ClientInboxLeadGroup,
  ExpertInboxProject,
  ProjectSide,
} from "@/v2Projects/types";
import { buildProjectUserError } from "@/v2Projects/userErrors";

const props = withDefaults(
  defineProps<{
    side: ProjectSide;
    isLoading: boolean;
    error: Error | null;
    clientGroups: ClientInboxLeadGroup[];
    expertProjects: ExpertInboxProject[];
    totalCount: number;
    emptyTitle?: string;
    emptyDescription?: string;
  }>(),
  {
    emptyTitle: "No projects yet",
    emptyDescription:
      "Once your requests are matched with experts, your projects and conversations will appear here.",
  },
);

const errorMessage = computed(() =>
  props.error ? buildProjectUserError("loadProject", props.error).description : null,
);

defineEmits<{
  retry: [];
  submit: [];
}>();
</script>

<template>
  <!-- Loading -->
  <div v-if="isLoading" class="text-center py-12">
    <div class="flex flex-col items-center gap-3">
      <div
        class="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"
      />
      <UiText variant="p" as="p" class="text-gray-500">
        Loading projects...
      </UiText>
    </div>
  </div>

  <!-- Error -->
  <div v-else-if="error" class="text-center py-12">
    <div class="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
      <UiIcon name="close" class="w-12 h-12 text-red-500 mx-auto mb-3" />
      <UiText variant="p" as="p" class="text-red-800 font-semibold mb-2">
        Projects could not be loaded
      </UiText>
      <UiText variant="p-small" as="p" class="text-red-600 mb-4">
        {{ errorMessage }}
      </UiText>
      <UiButton variant="outline" label="Try Again" @click="$emit('retry')" />
    </div>
  </div>

  <!-- Empty -->
  <div v-else-if="totalCount === 0" class="text-center py-12">
    <div class="flex flex-col items-center gap-4 max-w-md mx-auto">
      <div
        class="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center"
      >
        <UiIcon name="package-placeholder" class="w-10 h-10 text-gray-400" />
      </div>
      <div>
        <UiText variant="h6" as="h6" class="font-semibold mb-2">
          {{ emptyTitle }}
        </UiText>
        <UiText variant="p-small" as="p" class="text-gray-600 mb-4">
          {{ emptyDescription }}
        </UiText>
        <UiButton
          v-if="side === 'client'"
          variant="primary"
          label="Submit Your First Request"
          @click="$emit('submit')"
        />
      </div>
    </div>
  </div>

  <!-- Client grouped list -->
  <div v-else-if="side === 'client'" class="flex flex-col gap-4">
    <ClientProjectLeadCard
      v-for="group in clientGroups"
      :key="group.lead?.id ?? group.projects[0]?.id"
      :group="group"
    />
  </div>

  <!-- Expert flat list -->
  <div v-else class="flex flex-col gap-4">
    <ExpertProjectCard
      v-for="project in expertProjects"
      :key="project.id"
      :project="project"
    />
  </div>
</template>
